import { supabase } from '../lib/supabase';
import type { UserRole, UserInvitation } from '../types/user';
import { log } from '../lib/logger';

export const userService = {
  async getCurrentUserRole(): Promise<UserRole | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        log.debug('No user logged in');
        return null;
      }

      log.debug('Fetching role for user:', user.id, user.email);

      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        log.error('Supabase request failed', error);
        if (error.code === 'PGRST205') {
          return null;
        }
        return null;
      }

      log.debug('User role data:', data);
      return data;
    } catch (error) {
      log.error('Exception in getCurrentUserRole:', error);
      return null;
    }
  },

  async getAllUsers(): Promise<UserRole[]> {
    try {
      const { data, error } = await supabase.rpc('get_all_user_roles');

      if (error) {
        log.error('Error fetching users:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      log.error('Error in getAllUsers:', error);
      return [];
    }
  },

  async updateUserRole(userId: string, updates: Partial<UserRole>): Promise<void> {
    const { error } = await supabase.rpc('admin_update_user_role', {
      target_user_id: userId,
      new_role: updates.role || null,
      new_can_view_campaigns: updates.can_view_campaigns ?? null,
      new_can_edit_campaigns: updates.can_edit_campaigns ?? null,
      new_can_delete_campaigns: updates.can_delete_campaigns ?? null,
      new_can_view_clients: updates.can_view_clients ?? null,
      new_can_edit_clients: updates.can_edit_clients ?? null,
      new_can_view_influencers: updates.can_view_influencers ?? null,
      new_can_edit_influencers: updates.can_edit_influencers ?? null,
      new_can_view_payments: updates.can_view_payments ?? null,
      new_can_view_commissions: updates.can_view_commissions ?? null,
      new_can_edit_commissions: updates.can_edit_commissions ?? null,
      new_can_manage_users: updates.can_manage_users ?? null,
      new_can_view_statistics: updates.can_view_statistics ?? null,
      new_can_edit_others_campaigns: updates.can_edit_others_campaigns ?? null,
      new_can_view_others_campaigns: updates.can_view_others_campaigns ?? null,
      new_can_view_admin_campaigns: updates.can_view_admin_campaigns ?? null,
      new_can_delete_others_campaigns: updates.can_delete_others_campaigns ?? null,
      new_can_issue_invoices: updates.can_issue_invoices ?? null
    });

    if (error) {
      throw error;
    }
  },

  async deleteUser(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId }
      });

      if (error) {
        log.error('User deletion failed:', error);
        throw new Error(error.message || 'Failed to delete user');
      }

      log.debug('User deleted successfully:', data);
    } catch (error) {
      log.error('Error in deleteUser:', error);
      throw error;
    }
  },

  async sendInvitation(email: string, role: 'admin' | 'agent' | 'influencer' | 'content_hub', permissions: Record<string, boolean>, influencer_id?: string, ch_role?: string, ch_project_ids?: string[], name?: string): Promise<{
    success: boolean;
    emailSent: boolean;
    emailError?: { message: string; status?: number; name?: string } | null;
    inviteUrl: string;
    email: string;
    message: string;
  }> {
    // Import the new invite service dynamically
    const { sendInviteViaEdgeFunction } = await import('./inviteService');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://app.mohit.pt';
    const redirectTo = `${siteUrl}/onboarding`;

    const result = await sendInviteViaEdgeFunction({
      email,
      role,
      permissions: permissions as any,
      influencer_id,
      redirectTo,
      ch_role,
      ch_project_ids,
      name,
    });

    // Convert new response format to old format for compatibility
    if ('ok' in result && result.ok) {
      return {
        success: true,
        // The function reports when the email failed; the admin can then
        // copy the sign-in link and send it themselves.
        emailSent: result.emailSent !== false,
        emailError: result.emailError ?? null,
        inviteUrl: result.inviteUrl || redirectTo,
        email: result.email,
        message: result.message,
      };
    }

    // Handle error - throw with context for AdminPanel error handling
    const error = new Error(result.message || 'Failed to send invitation');
    const rawDetails = result.details;
    const safeDetails = rawDetails == null ? null
      : typeof rawDetails === 'string' ? rawDetails
      : JSON.stringify(rawDetails);
    (error as any).context = {
      body: {
        error: result.message,
        errorType: result.code,
        technicalDetails: {
          message: result.message || 'Unknown error',
          details: safeDetails,
        },
        actionableSteps: result.actionableSteps || [],
      }
    };
    throw error;
  },

  async getInvitations(): Promise<UserInvitation[]> {
    try {
      const { data, error } = await supabase.rpc('get_all_invitations');

      if (error) {
        log.error('Error fetching invitations:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      log.error('Error in getInvitations:', error);
      return [];
    }
  },

  async deleteInvitation(invitationId: string): Promise<void> {
    const { error } = await supabase.rpc('admin_delete_invitation', {
      invitation_id: invitationId
    });

    if (error) {
      throw error;
    }
  }
};
